// ============================================================
// HandoutsDrawer (v8.363.10) - the GM's pile and what I am shown.
//
// A drawer over the target panel. The GM drops images and PDFs, writes
// notes and links, and decides who sees each; everyone else sees only
// the cards whose audience admits them. A refusal (the wrong kind, past
// the per-file limit, a full session, a backend without file storage) is a
// sentence under the tools, never a silent nothing; a batch posts every
// file that fits and names the rest in one line. A whole folder - picked,
// or dropped - lands each file in a handout folder named from its path.
// ============================================================
import { useMemo, useRef, useState, type ChangeEvent, type DragEvent, type ReactNode } from 'react';
import common from '../styles/common.module.css';
import styles from '../styles/HandoutsDrawer.module.css';
import clsx from '../utils/clsx';
import { useHandoutsContext, type HandoutsContextValue } from '../contexts/HandoutsContext';
import {
  MAX_FILE_BYTES,
  allTags,
  batchSummary,
  folderItems,
  folderTree,
  formatBytes,
  sizeLabel,
  type BatchItem,
  type Handout,
  type HandoutSort,
  type Usage,
} from '../engine/handouts';
import HandoutForm, { type HandoutFormValues } from './HandoutForm';
import HandoutRevealDialog from './HandoutRevealDialog';
import HandoutFolders from './HandoutFolders';
import HandoutFromLibrary from './HandoutFromLibrary';
import { DIRECTORY_INPUT } from '../utils/directoryInput';
import { droppedFiles } from '../utils/droppedFiles';

const ACCEPT = 'image/png,image/jpeg,image/webp,image/gif,application/pdf,.png,.jpg,.jpeg,.webp,.gif,.pdf';

/** The session's ceiling, and what fills it (v8.363.11). A rule cannot sum; this is the GM's only gauge. */
function UsageMeter({ usage }: { usage: Usage }) {
  const pct = Math.min(100, Math.round(usage.fraction * 100));
  const kinds = (['image', 'pdf', 'note', 'link'] as const)
    .filter((k) => usage.byKind[k].count > 0)
    .map(
      (k) =>
        `${usage.byKind[k].count} ${k}${usage.byKind[k].count === 1 ? '' : 's'} ${formatBytes(usage.byKind[k].bytes)}`,
    )
    .join(' · ');
  return (
    <span className={styles.usage} data-testid="handouts-usage" title={kinds || 'Nothing yet'}>
      <span className={clsx(styles.meter, pct >= 90 && styles.meterHot)} aria-hidden="true">
        <span style={{ width: `${pct}%` }} />
      </span>
      {formatBytes(usage.bytes)} of {formatBytes(usage.limit)} · {usage.count}
    </span>
  );
}

export default function HandoutsDrawer() {
  const ctx = useHandoutsContext();
  return ctx && ctx.open ? <Drawer ctx={ctx} /> : null;
}

const messageOf = (e: unknown) => (e instanceof Error ? e.message : String(e));

/**
 * The drawer's one error line: `attempt` clears it, runs the action, and
 * shows a refusal as its sentence (the wrong kind, past the per-file
 * limit, a full session...) rather than letting it vanish.
 */
function useRefusal() {
  const [error, setError] = useState<string | null>(null);
  const attempt = async (action: () => Promise<unknown>) => {
    setError(null);
    try {
      await action();
    } catch (e) {
      setError(messageOf(e));
    }
  };
  return { error, attempt };
}

type Refusal = ReturnType<typeof useRefusal>;

/** Select mode (v8.363.11): `picked` is null while off, a set of ids while on. Delete arms. */
function useSelection() {
  const [picked, setPicked] = useState<Set<string> | null>(null);
  const [armed, setArmed] = useState(false);
  const pick = (id: string, on: boolean) =>
    setPicked((cur) => {
      const next = new Set(cur ?? []);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  const stop = () => {
    setPicked(null);
    setArmed(false);
  };
  return { picked, setPicked, armed, setArmed, pick, stop };
}

type Selection = ReturnType<typeof useSelection>;
type FormState = { kind: 'note' | 'link'; editing: Handout | null };

function DrawerHead({ ctx }: { ctx: HandoutsContextValue }) {
  return (
    <div className={styles.head}>
      <strong>Handouts</strong>
      {ctx.unread.size > 0 && (
        <span className={styles.newCount} data-testid="handouts-new">
          {ctx.unread.size} new
        </span>
      )}
      {ctx.isGm && <UsageMeter usage={ctx.usage} />}
      <button
        type="button"
        className={common.btnSmall}
        onClick={ctx.close}
        aria-label="Close handouts"
        data-testid="handouts-close"
      >
        ✕
      </button>
    </div>
  );
}

/** A button that opens a hidden file input: some files, or (`directory`) a whole folder. */
function FilePicker(props: {
  label: string;
  disabled: boolean;
  directory?: boolean;
  testId: string;
  onFiles: (files: FileList) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const onPick = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) props.onFiles(e.target.files);
    e.target.value = '';
  };
  return (
    <>
      <button
        type="button"
        className={common.btnSmall}
        onClick={() => inputRef.current?.click()}
        disabled={props.disabled}
      >
        {props.label}
      </button>
      <input
        ref={inputRef}
        type="file"
        multiple
        {...(props.directory ? DIRECTORY_INPUT : { accept: ACCEPT })}
        onChange={onPick}
        className={styles.fileInput}
        data-testid={props.testId}
      />
    </>
  );
}

/** The GM's tools: upload some files or a whole folder, from the library, a new note, a new link. */
function GmTools({
  busy,
  onFiles,
  onFolder,
  onNew,
  fromLibrary,
}: {
  busy: HandoutsContextValue['busy'];
  onFiles: (files: FileList) => void;
  onFolder: (files: FileList) => void;
  onNew: (kind: FormState['kind']) => void;
  /** "From my library…", for a GM whose account has one. */
  fromLibrary?: ReactNode;
}) {
  return (
    <div className={styles.tools}>
      <FilePicker
        label={busy ? 'Uploading…' : 'Add image or PDF'}
        disabled={!!busy}
        testId="handout-file"
        onFiles={onFiles}
      />
      <FilePicker label="Add folder" disabled={!!busy} directory testId="handout-folder" onFiles={onFolder} />
      {fromLibrary}
      <button type="button" className={common.btnSmall} onClick={() => onNew('note')} data-testid="handout-new-note">
        New note
      </button>
      <button type="button" className={common.btnSmall} onClick={() => onNew('link')} data-testid="handout-new-link">
        New link
      </button>
      <span className={styles.hint}>or drop files or a folder here · {sizeLabel(MAX_FILE_BYTES)} each</span>
    </div>
  );
}

/** Select mode's buttons: All, the arming Delete, and Done. */
function SelectModeButtons({
  selection,
  picked,
  allIds,
  onDelete,
}: {
  selection: Selection;
  picked: Set<string>;
  allIds: () => string[];
  onDelete: () => void;
}) {
  return (
    <>
      <button
        type="button"
        className={common.btnSmall}
        onClick={() => selection.setPicked(new Set(allIds()))}
        data-testid="handouts-select-all"
      >
        All
      </button>
      {selection.armed ? (
        <button
          type="button"
          className={clsx(common.btnSmall, styles.danger)}
          onClick={onDelete}
          data-testid="handouts-delete-confirm"
        >
          Delete {picked.size} for good
        </button>
      ) : (
        <button
          type="button"
          className={common.btnSmall}
          disabled={picked.size === 0}
          onClick={() => selection.setArmed(true)}
          data-testid="handouts-delete-selected"
        >
          Delete {picked.size || ''}
        </button>
      )}
      <button type="button" className={common.btnSmall} onClick={selection.stop}>
        Done
      </button>
    </>
  );
}

/** The GM's way into select mode, or its buttons while it is on. */
function SelectControls(props: { selection: Selection; allIds: () => string[]; onDelete: () => void }) {
  const { picked, setPicked } = props.selection;
  if (picked !== null) return <SelectModeButtons {...props} picked={picked} />;
  return (
    <button
      type="button"
      className={common.btnSmall}
      onClick={() => setPicked(new Set())}
      data-testid="handouts-select"
    >
      Select…
    </button>
  );
}

function SortPicker({ sort, onSort }: { sort: HandoutSort; onSort: (sort: HandoutSort) => void }) {
  return (
    <label className={styles.hint}>
      Sort{' '}
      <select
        className={common.input}
        value={sort}
        onChange={(e) => onSort(e.target.value as HandoutSort)}
        aria-label="Sort handouts"
        data-testid="handouts-sort"
      >
        <option value="newest">newest first</option>
        <option value="largest">largest first</option>
        <option value="title">by title</option>
      </select>
    </label>
  );
}

function TagFilterRow({
  tags,
  filter,
  onToggle,
}: {
  tags: string[];
  filter: string[];
  onToggle: (tag: string) => void;
}) {
  if (tags.length === 0) return null;
  return (
    <div className={styles.tagRow}>
      {tags.map((t) => (
        <button
          type="button"
          key={t}
          className={clsx(styles.tag, filter.includes(t) && styles.tagOn)}
          onClick={() => onToggle(t)}
          aria-pressed={filter.includes(t)}
        >
          {t}
        </button>
      ))}
    </div>
  );
}

/** Saves the open form: an edit, a new note or a new link. */
function saveForm(ctx: HandoutsContextValue, form: FormState, v: HandoutFormValues) {
  if (form.editing) return ctx.update(form.editing.id, v);
  return form.kind === 'note' ? ctx.postNote(v) : ctx.postLink(v);
}

/** The note/link form: which one is open, and saving it (which closes it on success). */
function useHandoutForm(ctx: HandoutsContextValue, attempt: Refusal['attempt']) {
  const [form, setForm] = useState<FormState | null>(null);
  const save = async (v: HandoutFormValues) => {
    if (!form) return;
    await attempt(async () => {
      await saveForm(ctx, form, v);
      setForm(null);
    });
  };
  return { form, setForm, save };
}

/** Dropping files or folders on the drawer uploads them - for the GM only; nobody else sees the highlight. */
function useDropZone(enabled: boolean, onDrop: (dt: DragEvent['dataTransfer']) => void) {
  const [dragging, setDragging] = useState(false);
  const handlers = {
    onDragOver: (e: DragEvent) => {
      if (!enabled) return;
      e.preventDefault();
      setDragging(true);
    },
    onDragLeave: () => setDragging(false),
    onDrop: (e: DragEvent) => {
      e.preventDefault();
      setDragging(false);
      if (enabled) onDrop(e.dataTransfer);
    },
  };
  return { dragging, handlers };
}

/** Sorting for everyone, and select mode for the GM, once there is anything to sort. */
function ListTools({
  ctx,
  sort,
  onSort,
  selection,
  onDelete,
}: {
  ctx: HandoutsContextValue;
  sort: HandoutSort;
  onSort: (sort: HandoutSort) => void;
  selection: Selection;
  onDelete: () => void;
}) {
  if (ctx.handouts.length === 0) return null;
  return (
    <div className={styles.tools}>
      <SortPicker sort={sort} onSort={onSort} />
      {ctx.isGm && (
        <SelectControls selection={selection} allIds={() => ctx.handouts.map((h) => h.id)} onDelete={onDelete} />
      )}
    </div>
  );
}

/** The GM's reveal dialog for the card whose Show... was pressed. */
function RevealDialog({
  ctx,
  revealing,
  onClose,
}: {
  ctx: HandoutsContextValue;
  revealing: string | null;
  onClose: () => void;
}) {
  if (!ctx.isGm) return null;
  const revealTarget = revealing ? (ctx.handouts.find((h) => h.id === revealing) ?? null) : null;
  return (
    <HandoutRevealDialog
      show={!!revealTarget}
      handout={revealTarget}
      people={ctx.people}
      channels={ctx.channels}
      onReveal={(target, announce) => ctx.reveal(revealing!, target, ctx.channels, { announce })}
      onPresent={(target, announce) => ctx.reveal(revealing!, target, ctx.channels, { announce, present: true })}
      onClose={onClose}
    />
  );
}

/** Uploading files and folders and deleting the selection, each reporting through the drawer's error line. */
function useBulkActions(ctx: HandoutsContextValue, attempt: Refusal['attempt'], selection: Selection) {
  // Files go up one at a time and every one that fits lands; what did
  // not is one line on the error line (thrown, so `attempt` shows it). A
  // drop's items arrive as a promise: its folders are walked first.
  const post = (items: readonly BatchItem[] | Promise<readonly BatchItem[]>) =>
    attempt(async () => {
      const line = batchSummary(await ctx.postFiles(await items));
      if (line) throw new Error(line);
    });
  const deletePicked = async () => {
    if (!selection.picked?.size) return;
    const ids = [...selection.picked];
    selection.stop();
    await attempt(() => ctx.removeMany(ids));
  };
  // The three ways files arrive: picked, a picked folder, or a drop (files or folders).
  const postList = (files: FileList) => void post(Array.from(files, (file) => ({ file })));
  const postFolder = (files: FileList) =>
    void post(folderItems(Array.from(files, (file) => ({ file, path: file.webkitRelativePath }))));
  const postDrop = (dt: DragEvent['dataTransfer']) => void post(droppedFiles(dt).then(folderItems));
  return { postList, postFolder, postDrop, deletePicked };
}

const toggled = (list: string[], t: string) => (list.includes(t) ? list.filter((x) => x !== t) : [...list, t]);

function Drawer({ ctx }: { ctx: HandoutsContextValue }) {
  const [revealing, setRevealing] = useState<string | null>(null);
  const [tagFilter, setTagFilter] = useState<string[]>([]);
  const [sort, setSort] = useState<HandoutSort>('newest');
  const { error, attempt } = useRefusal();
  const selection = useSelection();
  const { form, setForm, save } = useHandoutForm(ctx, attempt);
  const handouts = ctx.handouts;
  const tags = useMemo(() => allTags(handouts), [handouts]);
  const folders = useMemo(() => folderTree(handouts, tagFilter, sort), [handouts, tagFilter, sort]);

  const { postList, postFolder, postDrop, deletePicked } = useBulkActions(ctx, attempt, selection);
  const drop = useDropZone(ctx.isGm, postDrop);

  return (
    <aside
      className={clsx(styles.drawer, drop.dragging && styles.dragging)}
      aria-label="Handouts"
      data-testid="handouts-drawer"
      {...drop.handlers}
    >
      <DrawerHead ctx={ctx} />
      {ctx.isGm && (
        <GmTools
          busy={ctx.busy}
          onFiles={postList}
          onFolder={postFolder}
          onNew={(kind) => setForm({ kind, editing: null })}
          fromLibrary={
            ctx.library && <HandoutFromLibrary library={ctx.library} handouts={handouts} attempt={attempt} />
          }
        />
      )}
      <ListTools ctx={ctx} sort={sort} onSort={setSort} selection={selection} onDelete={() => void deletePicked()} />
      {error && (
        <div className={styles.error} role="alert" data-testid="handouts-error">
          {error}
        </div>
      )}
      {form && (
        <HandoutForm
          kind={form.kind}
          initial={form.editing ?? undefined}
          onSave={save}
          onCancel={() => setForm(null)}
        />
      )}
      <TagFilterRow tags={tags} filter={tagFilter} onToggle={(t) => setTagFilter((cur) => toggled(cur, t))} />
      <HandoutFolders
        ctx={ctx}
        folders={folders}
        picked={selection.picked}
        onEdit={(h) => setForm({ kind: h.kind === 'link' ? 'link' : 'note', editing: h })}
        onReveal={setRevealing}
        onPick={selection.pick}
      />
      <RevealDialog ctx={ctx} revealing={revealing} onClose={() => setRevealing(null)} />
    </aside>
  );
}
